import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Public routes live at /public/... (no /api prefix).
// Strip trailing /api if REACT_APP_BACKEND_URL includes it.
const RAW_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = RAW_BASE.endsWith('/api') ? RAW_BASE.slice(0, -4) : RAW_BASE.replace(/\/api\/?$/, '');

export default function ApplyPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loadingJob, setLoadingJob] = useState(true);
  const [jobError, setJobError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    linkedin_url: '',
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    axios.get(`${API_BASE}/public/job/${jobId}`)
      .then(r => setJob(r.data))
      .catch(() => setJobError('This job posting is no longer available or the link is invalid.'))
      .finally(() => setLoadingJob(false));
  }, [jobId]);

  const validate = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = 'Full name is required';
    if (!form.email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) errs.email = 'Valid email is required';
    if (!resumeFile) errs.resume = 'Please upload your resume (PDF or DOCX)';
    return errs;
  };

  const handleFile = (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    const ext = file.name.toLowerCase();
    if (!allowed.includes(file.type) && !ext.endsWith('.pdf') && !ext.endsWith('.docx')) {
      setFieldErrors(prev => ({ ...prev, resume: 'Only PDF or DOCX files are accepted' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFieldErrors(prev => ({ ...prev, resume: 'File must be under 5MB' }));
      return;
    }
    setResumeFile(file);
    setFieldErrors(prev => { const n = { ...prev }; delete n.resume; return n; });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('full_name', form.full_name.trim());
      fd.append('email', form.email.trim());
      if (form.phone) fd.append('phone', form.phone.trim());
      if (form.linkedin_url) fd.append('linkedin_url', form.linkedin_url.trim());
      fd.append('resume', resumeFile);

      await axios.post(`${API_BASE}/public/apply/${jobId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 409) {
        setSubmitError('You have already applied for this role.');
      } else {
        setSubmitError(detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingJob) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading job details…</p>
        </div>
      </div>
    );
  }

  if (jobError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md text-center">
          <div className="text-4xl mb-4">🔍</div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Job Not Found</h2>
          <p className="text-sm text-slate-500">{jobError}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-teal-50 flex items-center justify-center">
            <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Application Submitted!</h2>
          <p className="text-slate-500 text-sm leading-relaxed">
            Thank you, <span className="font-semibold text-slate-700">{form.full_name}</span>.<br />
            We've received your application for <span className="font-semibold text-slate-700">{job?.title}</span>.<br />
            Our team will review your profile and be in touch soon.
          </p>
        </div>
      </div>
    );
  }

  const desc = job?.description || '';
  const descShort = desc.length > 400 ? desc.slice(0, 400) + '…' : desc;
  const skills = Array.isArray(job?.required_skills) ? job.required_skills : [];

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-teal-600 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor">
              <path d="M12 2a5 5 0 015 5v2a5 5 0 01-10 0V7a5 5 0 015-5zm-7 18a7 7 0 0114 0H5z" />
            </svg>
          </div>
          <span className="font-bold text-slate-800 text-base tracking-tight">Vocala.</span>
        </div>
        <span className="ml-auto text-xs text-slate-400">Powered by Vocala AI</span>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10">
        {/* Job Header Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 leading-tight">{job?.title}</h1>
              {job?.workspace_name && <p className="text-sm text-slate-500 mt-0.5">{job.workspace_name}</p>}
            </div>
          </div>

          {/* Meta pills */}
          <div className="flex flex-wrap gap-2 mb-4">
            {job?.location && (
              <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" /><circle cx="12" cy="11" r="3" /></svg>
                {job.location}
              </span>
            )}
            {job?.job_type && (
              <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" /></svg>
                {job.job_type}
              </span>
            )}
            {job?.experience_level && (
              <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                {job.experience_level}
              </span>
            )}
          </div>

          {/* Skills */}
          {skills.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {skills.map((s) => (
                <span key={s} className="text-xs bg-teal-50 text-teal-700 border border-teal-100 px-2.5 py-0.5 rounded-full font-medium">{s}</span>
              ))}
            </div>
          )}

          {/* Description */}
          {desc && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 mb-1.5">About the Role</h2>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                {showFullDesc ? desc : descShort}
              </p>
              {desc.length > 400 && (
                <button onClick={() => setShowFullDesc(v => !v)}
                  className="text-xs text-teal-600 hover:text-teal-700 font-semibold mt-1.5">
                  {showFullDesc ? 'Show less ↑' : 'Show more ↓'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Apply Form Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1">Apply for this Role</h2>
          <p className="text-xs text-slate-400 mb-5">Fields marked * are required</p>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Full Name *</label>
                <input type="text" value={form.full_name}
                  onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="Jane Smith"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 ${fieldErrors.full_name ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                />
                {fieldErrors.full_name && <p className="text-xs text-red-500 mt-1">{fieldErrors.full_name}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Email *</label>
                <input type="email" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="jane@example.com"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 ${fieldErrors.email ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                />
                {fieldErrors.email && <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Phone</label>
                <input type="tel" value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+1 555 000 0000"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">LinkedIn URL</label>
                <input type="url" value={form.linkedin_url}
                  onChange={e => setForm(p => ({ ...p, linkedin_url: e.target.value }))}
                  placeholder="linkedin.com/in/janesmith"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400"
                />
              </div>
            </div>

            {/* Resume Upload */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Resume *</label>
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative cursor-pointer border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                  dragOver ? 'border-teal-500 bg-teal-50' :
                  resumeFile ? 'border-teal-400 bg-teal-50/40' :
                  fieldErrors.resume ? 'border-red-400 bg-red-50' :
                  'border-slate-200 hover:border-teal-300 hover:bg-slate-50'
                }`}
              >
                <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc"
                  className="sr-only" onChange={e => handleFile(e.target.files[0])} />
                {resumeFile ? (
                  <>
                    <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-teal-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold text-teal-700">{resumeFile.name}</p>
                    <p className="text-xs text-teal-500 mt-0.5">{(resumeFile.size / 1024).toFixed(0)} KB · Click to change</p>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-slate-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold text-slate-600">Drop your resume here or <span className="text-teal-600">browse</span></p>
                    <p className="text-xs text-slate-400 mt-0.5">PDF or DOCX · Max 5MB</p>
                  </>
                )}
              </div>
              {fieldErrors.resume && <p className="text-xs text-red-500 mt-1">{fieldErrors.resume}</p>}
            </div>

            {submitError && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{submitError}</div>
            )}

            <button type="submit" disabled={submitting}
              className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl text-sm shadow-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {submitting ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting…</>
              ) : 'Apply Now →'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">This application is processed securely by Vocala AI</p>
      </main>
    </div>
  );
}
